import { formatHex, hsl, interpolate } from 'culori';
import { IShopCss } from '../types/contentful/types.generated';
import { defaultTreetStyles } from '../shopConfig/config';
import { autoLightenOrDarkenByPercent, isColorDark } from './colors/colors';

export const fallbackDefaultStyles = {
  popoverBackgroundColor: defaultTreetStyles.gray00,
  shopColor00: defaultTreetStyles.gray00,
  shopColor10: defaultTreetStyles.gray10,
  shopColor20: defaultTreetStyles.gray20,
  shopColor40: defaultTreetStyles.gray40,
  shopColor60: defaultTreetStyles.gray60,
  shopColor80: defaultTreetStyles.gray80,
  shopColor100: defaultTreetStyles.gray100,
};

const valueStepMap = {
  shopColor00: 0,
  shopColor10: 0.04,
  shopColor20: 0.2,
  shopColor40: 0.4,
  shopColor60: 0.6,
  shopColor80: 0.8,
  shopColor100: 1,
};

const PERCENT_CONTRAST_POPOVER = 5;

export type ShopColors = Record<keyof typeof valueStepMap, string> & {
  // For popovers, we cannot use the generated color scheme because the
  // popover effect relies on a darker box shadow.
  // On sites with darker backgrounds, flipping this would cause an undesirable white glow.
  // Instead, we use a lighter background color for these cases.
  popoverBackgroundColor: string;
};

export const generateDefaultShopStyles = (css?: IShopCss) => {
  if (!css) {
    return fallbackDefaultStyles;
  }

  const backgroundColor = css?.backgroundColor || '#ffffff';
  const textColor = css?.body1?.color || '#000000';

  // Convert colors to HSL color space
  const bgColorHsl = hsl(backgroundColor);
  const textColorHsl = hsl(textColor);

  if (!bgColorHsl || !textColorHsl) {
    throw new Error('Invalid colors provided for palette generation');
  }

  // Create interpolator between background and text color in HSL space
  const interpolateHsl = interpolate([bgColorHsl, textColorHsl], 'hsl');

  const shopColors: ShopColors = {} as ShopColors;

  Object.entries(valueStepMap).forEach(([key, step]) => {
    const interpolationValue = step;
    const interpolatedColor = interpolateHsl(interpolationValue);
    const hex = formatHex(interpolatedColor);
    shopColors[key as keyof ShopColors] = hex;
  });

  let popoverBgHex;
  // If bg color is dark (luminance is < 0.5), lighten the color.
  // If not, do nothing (shadows will make the popover distinct).
  if (isColorDark(backgroundColor)) {
    // Background color for surfaces that live above the background (i.e. modals, filter menus)
    popoverBgHex = autoLightenOrDarkenByPercent(backgroundColor, PERCENT_CONTRAST_POPOVER);
  }

  shopColors.popoverBackgroundColor = popoverBgHex || shopColors.shopColor00;

  return shopColors;
};
